"use strict";
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Colour, only as much as the contrast audit needs.                          │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Parses the two syntaxes `tokens.css` uses — `oklch()` and hex — to sRGB, and computes WCAG
 * contrast. Nothing here is for runtime use by a component: components name tokens, and the
 * browser resolves them. This exists so the audit can answer a question the browser will not
 * answer at build time.
 *
 * ⚠️ **A colour converter that is subtly wrong produces plausible ratios**, which is worse
 * than one that throws — a waiver granted against a wrong number outlives the mistake. So the
 * tests check known values (`oklch(1 0 0)` is white, black on white is 21:1) rather than
 * checking that the function returns something.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ON_ID_LIGHT = exports.ON_ID_INK = void 0;
exports.parseColor = parseColor;
exports.over = over;
exports.luminance = luminance;
exports.contrast = contrast;
exports.ratio = ratio;
exports.toLab = toLab;
exports.deltaE = deltaE;
exports.rgbDistance = rgbDistance;
exports.readableOn = readableOn;
const clamp01 = (n) => Math.min(1, Math.max(0, n));
/** oklab → linear sRGB. Björn Ottosson's matrices. */
function oklabToLinearRgb(L, a, b) {
    const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = L - 0.0894841775 * a - 1.291485548 * b;
    const l = l_ * l_ * l_;
    const m = m_ * m_ * m_;
    const s = s_ * s_ * s_;
    return [
        4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
        -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
        -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
    ];
}
/** Linear → sRGB gamma. */
function encodeGamma(channel) {
    const c = clamp01(channel);
    return c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055;
}
/** sRGB gamma → linear. */
function decodeGamma(channel) {
    return channel <= 0.04045 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
}
/** `0.12`, `12%` or absent → 1. */
function parseAlpha(raw) {
    if (raw === undefined || raw.trim() === '')
        return 1;
    const text = raw.trim();
    if (text.endsWith('%'))
        return clamp01(Number(text.slice(0, -1)) / 100);
    return clamp01(Number(text));
}
/**
 * Parse one CSS colour. Supports what `tokens.css` actually contains and refuses the rest —
 * ⚠️ **loudly**, because a parser that returns black for something it does not understand
 * makes every ratio involving that token wrong in the safe-looking direction.
 */
function parseColor(input) {
    const value = input.trim();
    const hex = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i.exec(value);
    if (hex) {
        const digits = hex[1];
        const full = digits.length === 3
            ? /* `#abc` -> `#aabbcc`. A regex rather than a spread: hex is ASCII, but the
                 lint rule is right in general and the regex is no less clear. */
                digits.replace(/(.)/g, '$1$1')
            : digits.length === 6
                ? digits
                : digits.slice(0, 6);
        const alpha = digits.length === 8 ? parseInt(digits.slice(6, 8), 16) / 255 : 1;
        return {
            r: parseInt(full.slice(0, 2), 16) / 255,
            g: parseInt(full.slice(2, 4), 16) / 255,
            b: parseInt(full.slice(4, 6), 16) / 255,
            a: alpha,
        };
    }
    /* `oklch(L C H)` and `oklch(L C H / A)`. L may be a percentage. */
    const oklch = /^oklch\(\s*([^\s]+)\s+([^\s]+)\s+([^\s/]+)\s*(?:\/\s*([^\s)]+))?\s*\)$/i.exec(value);
    if (oklch) {
        const rawL = oklch[1];
        const L = rawL.endsWith('%') ? Number(rawL.slice(0, -1)) / 100 : Number(rawL);
        const C = Number(oklch[2]);
        const H = Number(oklch[3]);
        if (!Number.isFinite(L) || !Number.isFinite(C) || !Number.isFinite(H)) {
            throw new Error(`oklch() with a non-numeric component: ${value}`);
        }
        const rad = (H * Math.PI) / 180;
        const [lr, lg, lb] = oklabToLinearRgb(L, C * Math.cos(rad), C * Math.sin(rad));
        return {
            r: encodeGamma(lr),
            g: encodeGamma(lg),
            b: encodeGamma(lb),
            a: parseAlpha(oklch[4]),
        };
    }
    throw new Error(`Unsupported colour syntax: "${value}". The audit understands hex and oklch(); ` +
        `add support here rather than letting it fall through to a default.`);
}
/** Composite a possibly-translucent colour over an opaque backdrop. */
function over(colour, backdrop) {
    if (colour.a >= 1)
        return colour;
    const mix = (c, b) => c * colour.a + b * (1 - colour.a);
    return {
        r: mix(colour.r, backdrop.r),
        g: mix(colour.g, backdrop.g),
        b: mix(colour.b, backdrop.b),
        a: 1,
    };
}
/** WCAG relative luminance. */
function luminance(colour) {
    return (0.2126 * decodeGamma(colour.r) + 0.7152 * decodeGamma(colour.g) + 0.0722 * decodeGamma(colour.b));
}
/**
 * WCAG contrast ratio, 1–21.
 *
 * ⚠️ Both arguments are composited over `backdrop` first. A translucent border on a dark card
 * — and `--border` is `oklch(1 0 0 / 12%)` in every dark theme — has no meaningful ratio
 * until it is composited, and treating it as opaque white reports 15:1 for something the eye
 * reads as a faint line.
 */
function contrast(foreground, background, backdrop) {
    const base = parseColor(backdrop ?? background);
    const bg = over(parseColor(background), base);
    const fg = over(parseColor(foreground), bg);
    const l1 = luminance(fg);
    const l2 = luminance(bg);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
}
/** Two decimals, the way a report should read. */
function ratio(value) {
    return `${value.toFixed(2)}:1`;
}
function toLab(colour) {
    const lin = (c) => decodeGamma(c);
    const r = lin(colour.r);
    const g = lin(colour.g);
    const b = lin(colour.b);
    /* sRGB → XYZ (D65), then XYZ → Lab against the D65 white point. */
    const x = (0.4124564 * r + 0.3575761 * g + 0.1804375 * b) / 0.95047;
    const y = 0.2126729 * r + 0.7151522 * g + 0.072175 * b;
    const z = (0.0193339 * r + 0.119192 * g + 0.9503041 * b) / 1.08883;
    const f = (t) => (t > 216 / 24389 ? Math.cbrt(t) : (841 / 108) * t + 4 / 29);
    const fx = f(x);
    const fy = f(y);
    const fz = f(z);
    return { l: 116 * fy - 16, a: 500 * (fx - fy), b: 200 * (fy - fz) };
}
/**
 * CIEDE2000. Two colours are "clearly different" from roughly 25 upward; 1 is the just-noticeable
 * difference under ideal conditions, and side-by-side UI fills are nowhere near ideal conditions.
 *
 * The formula is CIE 142-2001 verbatim — it is long, unlovely and not worth paraphrasing.
 */
function deltaE(first, second) {
    const one = toLab(parseColor(first));
    const two = toLab(parseColor(second));
    const rad = Math.PI / 180;
    const deg = 180 / Math.PI;
    const c1 = Math.hypot(one.a, one.b);
    const c2 = Math.hypot(two.a, two.b);
    const cBar = (c1 + c2) / 2;
    const g = 0.5 * (1 - Math.sqrt(cBar ** 7 / (cBar ** 7 + 25 ** 7)));
    const a1 = (1 + g) * one.a;
    const a2 = (1 + g) * two.a;
    const cp1 = Math.hypot(a1, one.b);
    const cp2 = Math.hypot(a2, two.b);
    const hue = (a, b) => {
        if (a === 0 && b === 0)
            return 0;
        const value = Math.atan2(b, a) * deg;
        return value < 0 ? value + 360 : value;
    };
    const h1 = hue(a1, one.b);
    const h2 = hue(a2, two.b);
    const dL = two.l - one.l;
    const dC = cp2 - cp1;
    let dh = 0;
    if (cp1 * cp2 !== 0) {
        dh = h2 - h1;
        if (dh > 180)
            dh -= 360;
        else if (dh < -180)
            dh += 360;
    }
    const dH = 2 * Math.sqrt(cp1 * cp2) * Math.sin((dh * rad) / 2);
    const lBar = (one.l + two.l) / 2;
    const cpBar = (cp1 + cp2) / 2;
    let hBar = h1 + h2;
    if (cp1 * cp2 !== 0) {
        if (Math.abs(h1 - h2) > 180)
            hBar += h1 + h2 < 360 ? 360 : -360;
        hBar /= 2;
    }
    const t = 1 -
        0.17 * Math.cos((hBar - 30) * rad) +
        0.24 * Math.cos(2 * hBar * rad) +
        0.32 * Math.cos((3 * hBar + 6) * rad) -
        0.2 * Math.cos((4 * hBar - 63) * rad);
    const sL = 1 + (0.015 * (lBar - 50) ** 2) / Math.sqrt(20 + (lBar - 50) ** 2);
    const sC = 1 + 0.045 * cpBar;
    const sH = 1 + 0.015 * cpBar * t;
    const rT = -2 *
        Math.sqrt(cpBar ** 7 / (cpBar ** 7 + 25 ** 7)) *
        Math.sin(60 * Math.exp(-(((hBar - 275) / 25) ** 2)) * rad);
    return Math.sqrt((dL / sL) ** 2 + (dC / sC) ** 2 + (dH / sH) ** 2 + rT * (dC / sC) * (dH / sH));
}
/**
 * Straight-line distance in sRGB, 0–441.
 *
 * ⚠️⚠️ **THIS IS THE METRIC THE THEME PACKAGE'S RECORDED NUMBERS WERE COMPUTED IN, AND IT IS NOT
 * THE ONE ITS PROSE NAMES.** The specification asks for "CIEDE2000 with a floor of 25, or RGB
 * Euclidean ≥ 60 as a cheap proxy", and then quotes distances of 20.6, 41.5, 75.0 and 96.9 for
 * specific pairs. Every one of those four reproduces here to the decimal; under CIEDE2000 the
 * same pairs measure 17.6 to 23.7, because two pastels are never a hundred units apart in a
 * perceptual space. The numbers are the evidence, so the gate uses the metric that produced them
 * — and reports `deltaE` beside it, which is the better measure and a different question.
 */
function rgbDistance(first, second) {
    const one = parseColor(first);
    const two = parseColor(second);
    return Math.hypot((one.r - two.r) * 255, (one.g - two.g) * 255, (one.b - two.b) * 255);
}
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE FOREGROUND FOR A COLOUR NOBODY CHOSE AT DESIGN TIME.             │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * An avatar, a board counter and a Gantt bar are painted with a colour that came out of the
 * DATABASE — a project's swatch, a member's hue, whatever somebody picked in the colour picker.
 * A token cannot answer what to write on it, because no token knows what it is.
 *
 * Nine components wrote `text-white` and were wrong on the light half of every palette; replacing
 * that with a single ink token was wrong on the dark half. **Measured in the browser across the
 * eight themes, `text-white` bottomed out at 1.36:1 and a single ink at 1.73:1** — on task keys
 * inside timeline bars and on avatar initials, in every theme, all the way through.
 *
 * So the choice is made per colour, at render, by measuring: whichever of the two carries the
 * better ratio wins. Two values rather than a scale, because a foreground that varies continuously
 * is a foreground nobody can gate — these two are checked against every colour the product ships
 * in `__tests__/readable-on.test.ts`.
 */
/**
 * ⚠️ **These MUST equal `--on-id` and `--on-id-light` in `tokens.css`, and a test asserts it.**
 * They are duplicated because this function is called with a runtime colour and returns a `var()`
 * for the caller to use — it cannot resolve a custom property to compare against.
 */
exports.ON_ID_INK = 'oklch(0.12 0.02 285)';
exports.ON_ID_LIGHT = 'oklch(0.98 0.002 286)';
/**
 * The better of the two foregrounds for `colour`, as the custom property to write.
 *
 * ⚠️ Returns a **token reference**, not a literal, so the value still lives in `tokens.css` and
 * the component stays free of colours. An unparseable colour falls back to the ink: a swatch the
 * parser does not understand is a bug to fix, not a reason to render invisible text.
 */
function readableOn(colour) {
    /* ⚠️ No fill means no override: the text inherits whatever the surface behind it decided.
     * Returning ink here would paint dark type on a dark card in five of the eight themes. */
    if (colour === undefined)
        return undefined;
    try {
        return contrast(exports.ON_ID_INK, colour) >= contrast(exports.ON_ID_LIGHT, colour)
            ? 'var(--on-id)'
            : 'var(--on-id-light)';
    }
    catch {
        return 'var(--on-id)';
    }
}
//# sourceMappingURL=color.js.map